import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

/**
 * VIB-721c: Mutations for `sidebar_shortcuts`-tabellen.
 * Brukes av SnarveierCard for å la admin/superadmin redigere snarveier per rolle.
 */

export interface CreateShortcutInput {
  role: string;
  label: string;
  path: string;
  icon: string;
  sort_order?: number;
  enabled?: boolean;
}

export interface UpdateShortcutInput {
  id: string;
  label?: string;
  path?: string;
  icon?: string;
  sort_order?: number;
  enabled?: boolean;
}

export function useCreateShortcut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateShortcutInput) => {
      const { data, error } = await supabase
        .from("sidebar_shortcuts")
        .insert({
          role: input.role,
          label: input.label,
          path: input.path,
          icon: input.icon,
          sort_order: input.sort_order ?? 0,
          enabled: input.enabled ?? true,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "sidebar_shortcuts");
    },
  });
}

export function useUpdateShortcut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateShortcutInput) => {
      const { id, ...rest } = input;
      const { error } = await supabase
        .from("sidebar_shortcuts")
        .update({ ...rest, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "sidebar_shortcuts");
    },
  });
}

export function useDeleteShortcut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sidebar_shortcuts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "sidebar_shortcuts");
    },
  });
}

/**
 * Re-sorts shortcuts within a role. Caller sends the full ordered list of
 * shortcut IDs; we assign sort_order = 1..N in that order.
 */
export function useReorderShortcuts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderedIds: string[] }) => {
      const updates = input.orderedIds.map((id, index) =>
        supabase
          .from("sidebar_shortcuts")
          .update({
            sort_order: index + 1,
            updated_at: new Date().toISOString(),
          })
          .eq("id", id),
      );
      const results = await Promise.all(updates);
      const firstError = results.find((r) => r.error);
      if (firstError?.error) throw firstError.error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "sidebar_shortcuts");
    },
  });
}
